<template>
	<FormGroup
		:id="$attrs.id"
		:label="label"
		:required="required"
		:help-text="helpText"
		v-slot="{ id }"
	>
		<div class="relative" v-click-outside="closeDropdown">
			<!-- Hidden input for native form submission -->
			<input v-if="name" type="hidden" :name="name" :value="value" />

			<div
				role="combobox"
				:aria-label="label"
				:aria-expanded="isOpen.toString()"
				:aria-controls="`dropdown-listbox-${id}`"
				:aria-haspopup="'listbox'"
				:aria-activedescendant="
					isOpen && focusedIndex >= 0
						? `dropdown-option-${id}-${focusedIndex}`
						: undefined
				"
				:aria-disabled="disabled"
				:aria-invalid="!!error"
				class="flex items-center justify-between w-full px-4 transition-all duration-200 bg-white border rounded-lg outline-none cursor-pointer ring-2 focus:ring-neutral-200 ring-transparent h-11"
				:class="[
					isOpen
						? 'border-neutral-800'
						: 'border-neutral-200 hover:border-neutral-300',
					disabled
						? 'bg-neutral-50 cursor-not-allowed opacity-60'
						: '',
					error ? 'border-red-500' : ''
				]"
				:id="`form-input-${id}`"
				@click="!disabled && toggleDropdown()"
				:tabindex="disabled ? -1 : 0"
				@keydown.enter.prevent="handleEnter()"
				@keydown.space.prevent="handleEnter()"
				@keydown.escape="closeDropdown()"
				@keydown.tab="closeDropdown()"
				@keydown.down.prevent="onKeyDown()"
				@keydown.up.prevent="onKeyUp()"
				@focus="onFocus()"
				@blur="onBlur()"
			>
				<span
					class="text-base leading-normal transition-colors duration-150"
					:class="
						selectedOption
							? isOpen || isFocused
								? 'text-neutral-800'
								: 'text-neutral-900'
							: 'text-neutral-400'
					"
				>
					{{
						selectedOption ? selectedOption.label : placeholderText
					}}
				</span>

				<svg
					class="flex-shrink-0 transition-transform duration-150 text-neutral-400"
					:class="{ 'rotate-180': isOpen }"
					width="20"
					height="20"
					viewBox="0 0 20 20"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden="true"
				>
					<path
						d="M5 7.5L10 12.5L15 7.5"
						stroke="currentColor"
						stroke-width="1.5"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
			</div>

			<transition
				enter-active-class="transition duration-150 ease-out"
				enter-class="-translate-y-1 opacity-0"
				enter-to-class="translate-y-0 opacity-100"
				leave-active-class="transition duration-100 ease-in"
				leave-class="translate-y-0 opacity-100"
				leave-to-class="-translate-y-1 opacity-0"
			>
				<ul
					v-if="isOpen"
					:id="`dropdown-listbox-${id}`"
					role="listbox"
					:aria-label="label"
					class="absolute left-0 right-0 z-50 py-2 mt-1 overflow-y-auto bg-white border rounded-lg shadow-lg top-full border-neutral-200 max-h-60"
				>
					<li
						v-if="options.length === 0"
						class="px-4 py-3 text-base text-center text-neutral-400"
						role="option"
						aria-disabled="true"
					>
						{{ $t('form.no-options-available') }}
					</li>
					<li
						v-for="(option, index) in options"
						:key="option.value"
						:id="`dropdown-option-${id}-${index}`"
						role="option"
						:aria-selected="option.value === value"
						class="block w-full px-4 py-3 text-base text-left transition-colors duration-100 bg-transparent cursor-pointer text-neutral-900 hover:bg-neutral-50"
						:class="[
							option.value === value
								? 'bg-neutral-100 font-medium'
								: '',
							index === focusedIndex ? 'bg-neutral-50' : ''
						]"
						@click="selectOption(option)"
						@mouseenter="focusedIndex = index"
					>
						{{ option.label }}
					</li>
				</ul>
			</transition>
		</div>
		<p
			v-if="error"
			class="mt-1.5 text-sm leading-normal text-red-500"
			role="alert"
		>
			{{ error }}
		</p>
	</FormGroup>
</template>

<script>
import FormGroup from '~/components/ui/form/FormGroup'

export default {
	name: 'Dropdown',
	components: {
		FormGroup
	},
	props: {
		label: {
			type: String,
			default: null
		},
		name: {
			type: String,
			default: null
		},
		value: {
			default: null
		},
		options: {
			type: Array,
			default: () => [],
			validator: options =>
				options.every(
					opt =>
						typeof opt === 'object' &&
						'value' in opt &&
						'label' in opt
				)
		},
		placeholder: {
			type: String,
			default: null
		},
		helpText: {
			type: String,
			default: null
		},
		required: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		error: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			isOpen: false,
			isFocused: false,
			focusedIndex: -1
		}
	},
	computed: {
		selectedOption() {
			return this.options.find(option => option.value === this.value)
		},
		placeholderText() {
			return this.placeholder || this.$t('form.select-option')
		}
	},
	methods: {
		toggleDropdown() {
			if (this.disabled) return
			if (this.isOpen) {
				this.closeDropdown()
				return
			}
			this.isOpen = true
			this.focusedIndex = this.options.findIndex(
				option => option.value === this.value
			)
			if (this.focusedIndex === -1) this.focusedIndex = 0
		},
		closeDropdown() {
			this.isOpen = false
			this.focusedIndex = -1
		},
		selectOption(option) {
			if (this.disabled) return
			this.$emit('input', option.value)
			this.$emit('change', option.value)
			this.closeDropdown()
		},
		onKeyDown() {
			if (this.disabled) return
			if (!this.isOpen) {
				this.toggleDropdown()
				return
			}
			if (this.focusedIndex < this.options.length - 1) {
				this.focusedIndex++
			}
		},
		onKeyUp() {
			if (this.disabled) return
			if (!this.isOpen) {
				this.toggleDropdown()
				return
			}
			if (this.focusedIndex > 0) {
				this.focusedIndex--
			}
		},
		handleEnter() {
			if (this.disabled) return
			if (
				this.isOpen &&
				this.focusedIndex >= 0 &&
				this.options[this.focusedIndex]
			) {
				this.selectOption(this.options[this.focusedIndex])
			} else {
				this.toggleDropdown()
			}
		},
		onFocus(event) {
			this.isFocused = true
			this.$emit('focus', event)
		},
		onBlur(event) {
			this.isFocused = false
			this.$emit('blur', event)
		}
	},
	watch: {
		options: 'closeDropdown',
		disabled(value) {
			if (value) this.closeDropdown()
		},
		focusedIndex(index) {
			if (this.isOpen && index >= 0 && this.options[index]) {
				this.$nextTick(() => {
					const menu = this.$el.querySelector('[role="listbox"]')
					const item = menu?.querySelectorAll('[role="option"]')[
						index
					]
					if (item) {
						item.scrollIntoView({ block: 'nearest' })
					}
				})
			}
		}
	}
}
</script>
